<!--
    מקורות לידים - the settings-page list editor (הגדרות → ראשי → מקורות לידים).

    The rows ARE $store.state.leadSources (the list every lead picker + the dashboard מקור column read),
    so a change here shows up in the customer pages at once. Saves happen immediately, one call per
    action, through the same lead-sources contract the ניהול מהיר modal uses:
      POST   lead-sources        {name}          create   (ListsHandler.createListItem pushes into the store)
      PATCH  lead-sources/:id    {name}          rename
      DELETE lead-sources/:id                    hide     (ListsHandler.deleteItem flags hidden in the store)
      PATCH  lead-sources/:id    {hidden:false}  restore a removed source
      GET    lead-sources/usage                  פניות count + last date per source

    A business can hold dozens of sources (86 on the biggest account, 26/10/2026), so both lists show
    10 rows a page (ListPager) and a search box filters them once either list is longer than a page.
    After an add / rename / restore the row may sort onto another page - revealSource opens that page.
-->
<template>
    <div class="leadSourcesSettings">

        <div style="color:#777;margin-bottom:12px;">
            המקורות שמוגדרים כאן מוצעים בשדה "מקור הליד" בפתיחת פנייה חדשה ובכרטיס הלקוח, ומופיעים בעמודת
            "מקור" בדף הפניות, בדשבורד ובדוחות. פניות שנכנסות אוטומטית (טופס באתר, פייסבוק, לידים במייל)
            מקבלות את המקור שלהן לפי השם. שינוי שם מתעדכן בכל הפניות הקיימות, והשינויים נשמרים מיד.
        </div>

        <div class="leadSourcesToolbar">
            <div class="leadSourcesAddRow">
                <input class="form-control leadSourceNewInput"
                       v-model="newName"
                       maxlength="255"
                       placeholder="שם מקור חדש, למשל: אינסטגרם, המלצה מלקוח, גוגל"
                       @keydown.enter.prevent="addSource()">
                <button class="btn btn-primary leadSourceAddButton"
                        :disabled="!newName.trim() || adding"
                        @click="addSource()">
                    הוסף מקור
                </button>
            </div>

            <label v-if="searchable"
                   class="leadSourcesSearch">
                <i class="fa-solid fa-magnifying-glass"></i>
                <input type="search"
                       class="leadSourcesSearchInput"
                       v-model="search"
                       placeholder="חיפוש מקור"
                       autocomplete="off"
                       @input="toFirstPages()">
            </label>
        </div>

        <table v-if="activeSources.length"
               class="table table-sm table-hover leadSourcesTable">
            <thead>
                <tr>
                    <th>מקור</th>
                    <th class="leadSourcesNumCol">פניות</th>
                    <th class="leadSourcesDateCol">פנייה אחרונה</th>
                    <th style="width:36px"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="source in pagedSources"
                    :key="source.id"
                    class="leadSourceRow"
                    :class="{ leadSourceRowFlash: flashId == source.id }"
                    :data-source-id="source.id">
                    <td>
                        <input v-if="editingId == source.id"
                               :ref="'editInput' + source.id"
                               class="form-control form-control-sm leadSourceEditInput"
                               v-model="editName"
                               maxlength="255"
                               @keydown.enter.prevent.stop="saveEdit(source)"
                               @keydown.esc.prevent="cancelEdit()"
                               @blur="saveEdit(source)">
                        <span v-else
                              class="leadSourceName"
                              title="לחצו לעריכת השם"
                              @click="startEdit(source)">
                            {{ source.name }}
                            <i class="fa-regular fa-pen-to-square leadSourceEditIcon"></i>
                        </span>
                    </td>
                    <td class="leadSourcesNumCol">{{ usageOf(source.id).events }}</td>
                    <td class="leadSourcesDateCol">{{ formatDate(usageOf(source.id).lastAt) }}</td>
                    <td>
                        <i class="fa-regular fa-trash-can trashButton"
                           style="cursor:pointer;color:#999"
                           title="הסרת המקור מהרשימה"
                           @click="removeSource(source)"></i>
                    </td>
                </tr>
                <tr v-if="!filteredSources.length"
                    class="leadSourcesNoMatch">
                    <td colspan="4"
                        style="color:#999;">
                        אין מקור פעיל שמתאים לחיפוש.
                    </td>
                </tr>
            </tbody>
        </table>

        <div v-else
             class="leadSourcesEmpty"
             style="color:#999;margin-bottom:10px;">
            עדיין לא הוגדרו מקורות לידים.
        </div>

        <ListPager v-if="activeSources.length"
                   class="leadSourcesPager"
                   :value="currentPage"
                   :total="filteredSources.length"
                   :pageSize="pageSize"
                   @input="page = $event"></ListPager>

        <div v-if="filteredHiddenSources.length"
             class="leadSourcesHiddenBlock">
            <span class="leadSourcesHiddenToggle"
                  style="color:#ab8ce4;cursor:pointer"
                  @click="showHidden = !showHidden">
                {{ showHidden ? 'הסתרת המקורות שהוסרו' : 'הצגת מקורות שהוסרו (' + filteredHiddenSources.length + ')' }}
            </span>

            <table v-if="showHidden"
                   class="table table-sm leadSourcesTable leadSourcesHiddenTable"
                   style="margin-top:8px;">
                <tbody>
                    <tr v-for="source in pagedHiddenSources"
                        :key="source.id"
                        class="leadSourceHiddenRow"
                        :data-source-id="source.id">
                        <td style="color:#999;">{{ source.name }}</td>
                        <td class="leadSourcesNumCol"
                            style="color:#999;">{{ usageOf(source.id).events }}</td>
                        <td class="leadSourcesDateCol"
                            style="color:#999;">{{ formatDate(usageOf(source.id).lastAt) }}</td>
                        <td style="width:60px;">
                            <span class="leadSourceRestore"
                                  style="color:#ab8ce4;cursor:pointer"
                                  @click="restoreSource(source)">
                                שחזור
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <ListPager v-if="showHidden"
                       class="leadSourcesPager"
                       :value="currentHiddenPage"
                       :total="filteredHiddenSources.length"
                       :pageSize="pageSize"
                       @input="hiddenPage = $event"></ListPager>
        </div>

    </div>
</template>

<script>
import ListsHandler from '../../modules/ListsHandler';
import ListPager from '../Common/ListPager.vue';
import { apiRequest, apiPatch } from '../../core/http/axios_api_v2';

const PAGE_SIZE = 10;

export default {
    name: 'LeadSourcesSettings',

    components: {
        ListPager
    },

    data() {
        return {
            newName: '',
            adding: false,
            editingId: null,
            editName: '',
            showHidden: false,
            usage: {},      // id -> { events, lastAt } from GET lead-sources/usage
            search: '',
            pageSize: PAGE_SIZE,
            page: 0,        // ListPager pages, 0-based; read through currentPage / currentHiddenPage (kept in range)
            hiddenPage: 0,
            flashId: null,  // the row revealSource just brought into view
        };
    },

    computed: {
        activeSources() {
            return this.sortedByName((this.$store.state.leadSources || []).filter(s => !s.hidden));
        },
        hiddenSources() {
            return this.sortedByName((this.$store.state.leadSources || []).filter(s => s.hidden));
        },
        // still shown while a search is typed, so a list that shrank to one page can be cleared
        searchable() {
            return this.activeSources.length > PAGE_SIZE || this.hiddenSources.length > PAGE_SIZE || !!this.search;
        },
        filteredSources() {
            return this.matching(this.activeSources);
        },
        filteredHiddenSources() {
            return this.matching(this.hiddenSources);
        },
        currentPage() {
            return this.pageInRange(this.page, this.filteredSources.length);
        },
        currentHiddenPage() {
            return this.pageInRange(this.hiddenPage, this.filteredHiddenSources.length);
        },
        pagedSources() {
            return this.filteredSources.slice(this.currentPage * PAGE_SIZE, (this.currentPage + 1) * PAGE_SIZE);
        },
        pagedHiddenSources() {
            return this.filteredHiddenSources.slice(this.currentHiddenPage * PAGE_SIZE, (this.currentHiddenPage + 1) * PAGE_SIZE);
        }
    },

    mounted() {
        this.loadUsage();
    },

    beforeDestroy() {
        clearTimeout(this.flashTimer);
    },

    methods: {

        sortedByName(list) {
            return list.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'he'));
        },

        matching(list) {
            let key = this.normalize(this.search);
            return key ? list.filter(s => this.normalize(s.name).includes(key)) : list;
        },

        // a removal can empty the last page - fall back to the page before it
        pageInRange(page, count) {
            return Math.max(0, Math.min(page, Math.ceil(count / PAGE_SIZE) - 1));
        },

        toFirstPages() {
            this.page = 0;
            this.hiddenPage = 0;
        },

        // an added / renamed / restored source sorts by its name, maybe onto another page or out of the
        // search - open the page it landed on and flash the row, so the change is seen
        revealSource(id) {
            let source = this.activeSources.find(s => s.id == id);
            if (!source)
                return;
            if (!this.matching([source]).length)
                this.search = '';
            this.page = Math.floor(this.filteredSources.indexOf(source) / PAGE_SIZE);
            this.flashId = id;
            clearTimeout(this.flashTimer);
            this.flashTimer = setTimeout(() => {
                this.flashId = null;
            }, 2000);
        },

        loadUsage() {
            apiRequest('get', 'lead-sources/usage').then(rows => {
                let usage = {};
                (rows || []).forEach(r => {
                    usage[r.id] = { events: r.events || 0, lastAt: r.lastAt || null };
                });
                this.usage = usage;
            }).catch(() => { });
        },

        usageOf(id) {
            return this.usage[id] || { events: 0, lastAt: null };
        },

        formatDate(value) {
            if (!value)
                return '—';
            let d = new Date(value);
            if (isNaN(d.getTime()))
                return '—';
            return d.getDate() + '/' + (d.getMonth() + 1) + '/' + d.getFullYear();
        },

        normalize(name) {
            return String(name || '').trim().toLowerCase();
        },

        findByName(name, list) {
            let key = this.normalize(name);
            return list.find(s => this.normalize(s.name) == key);
        },

        warnDuplicate(name) {
            this.$swal({
                title: '"' + name + '" כבר נמצא ברשימה.',
                type: 'warning',
                confirmButtonColor: '#ab8ce4',
                confirmButtonText: 'המשך'
            });
        },

        addSource() {
            let name = this.newName.trim();
            if (!name || this.adding)
                return;

            if (this.findByName(name, this.activeSources)) {
                this.warnDuplicate(name);
                return;
            }

            // a source that was removed earlier comes back instead of getting a twin row
            let removed = this.findByName(name, this.hiddenSources);
            if (removed) {
                this.restoreSource(removed);
                this.newName = '';
                return;
            }

            this.adding = true;
            ListsHandler.createListItem(name, 'leadSources', 'lead-sources').then(created => {
                this.newName = '';
                if (created && created.id && !this.usage[created.id])
                    this.$set(this.usage, created.id, { events: 0, lastAt: null });
                if (created && created.id)
                    this.revealSource(created.id);
            }).catch(() => { }).finally(() => {
                this.adding = false;
            });
        },

        startEdit(source) {
            this.editingId = source.id;
            this.editName = source.name;
            this.$nextTick(() => {
                let refs = this.$refs['editInput' + source.id];
                let input = Array.isArray(refs) ? refs[0] : refs;
                if (input) {
                    input.focus();
                    input.select();
                }
            });
        },

        cancelEdit() {
            this.editingId = null;
            this.editName = '';
        },

        // Enter and the blur it causes both land here - the first call closes the editor, the second is a no-op.
        // The Enter keydown must not bubble past the input: the confirm swal it opens hooks window.onkeydown
        // synchronously and would treat the same keystroke as its own Enter (= auto-confirm).
        saveEdit(source) {
            if (this.editingId != source.id)
                return;
            this.editingId = null;

            let name = this.editName.trim();
            if (!name || name == source.name)
                return;

            let clash = this.findByName(name, this.activeSources);
            if (clash && clash.id != source.id) {
                this.warnDuplicate(name);
                return;
            }

            this.$swal({
                title: 'האם לשמור את השינוי?',
                text: 'בכל מקום שהופיע "' + source.name + '" יופיע מעכשיו "' + name + '".',
                showCancelButton: true,
                confirmButtonText: 'שמור',
                cancelButtonText: 'חזור',
                confirmButtonColor: '#ab8ce4'
            }).then(() => {
                let previous = source.name;
                source.name = name;     // the store row itself - every picker in the app shows the new name at once
                this.revealSource(source.id);
                apiPatch('lead-sources/' + source.id, { name }).catch(() => {
                    source.name = previous;
                });
            }).catch(() => { });
        },

        removeSource(source) {
            this.$swal({
                title: 'האם להסיר את המקור "' + source.name + '" מהרשימה?',
                html: 'פניות קיימות ישמרו את המקור שלהן, אך הוא לא יוצע יותר לבחירה. אפשר לשחזר אותו מרשימת המקורות שהוסרו.',
                showCancelButton: true,
                confirmButtonText: 'הסר',
                cancelButtonText: 'חזור',
                confirmButtonColor: '#ff5370'
            }).then(() => {
                if (this.editingId == source.id)
                    this.cancelEdit();
                // rows created in this session arrive without a hidden key - $set keeps the flag reactive
                this.$set(source, 'hidden', true);
                ListsHandler.deleteItem('lead-sources', source.id, 'leadSources');
            }).catch(() => { });
        },

        restoreSource(source) {
            this.$set(source, 'hidden', false);
            this.revealSource(source.id);
            apiPatch('lead-sources/' + source.id, { hidden: false }).catch(() => {
                this.$set(source, 'hidden', true);
            });
        }
    }
}
</script>

<style>
.leadSourcesSettings .leadSourcesToolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 16px;
    max-width: 720px;
    margin-bottom: 14px;
}

.leadSourcesSettings .leadSourcesAddRow {
    display: flex;
    gap: 8px;
    flex: 1 1 360px;
    max-width: 480px;
}

.leadSourcesSettings .leadSourcesSearch {
    position: relative;
    flex: 0 1 210px;
    margin: 0;
}

.leadSourcesSettings .leadSourcesSearch i {
    position: absolute;
    top: 50%;
    right: 11px;
    transform: translateY(-50%);
    color: #9a9fb1;
    font-size: 13px;
    pointer-events: none;
}

.leadSourcesSettings .leadSourcesSearchInput {
    width: 100%;
    height: 38px;
    padding: 0 32px 0 10px;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
    background: #fff;
    color: #2a3142;
    font-size: 14px;
    outline: none;
}

.leadSourcesSettings .leadSourcesSearchInput:focus {
    border-color: #ab8ce4;
    box-shadow: 0 0 0 3px rgba(171, 140, 228, .18);
}

.leadSourcesSettings .leadSourcesPager {
    max-width: 720px;
}

@keyframes leadSourceRowFlash {
    from {
        background-color: #ece3fb;
    }

    to {
        background-color: transparent;
    }
}

.leadSourcesSettings .leadSourceRowFlash > td {
    animation: leadSourceRowFlash 2s ease-out;
}

.leadSourcesSettings .leadSourceAddButton {
    white-space: nowrap;
}

.leadSourcesSettings .leadSourcesTable {
    max-width: 720px;
    margin: 0;
}

.leadSourcesSettings .leadSourcesNumCol,
.leadSourcesSettings .leadSourcesDateCol {
    text-align: center;
    white-space: nowrap;
}

.leadSourcesSettings .leadSourcesNumCol {
    width: 90px;
}

.leadSourcesSettings .leadSourcesDateCol {
    width: 140px;
}

.leadSourcesSettings .leadSourceName {
    cursor: pointer;
}

.leadSourcesSettings .leadSourceEditIcon {
    color: #ccc;
    font-size: 12px;
    margin-right: 6px;
    visibility: hidden;
}

.leadSourcesSettings .leadSourceRow:hover .leadSourceEditIcon {
    visibility: visible;
}

.leadSourcesSettings .leadSourceEditInput {
    max-width: 320px;
}

.leadSourcesSettings .leadSourcesHiddenBlock {
    margin-top: 10px;
}
</style>
